import { MILIMETERS_TO_INCHES } from '@/constants/measurementUnits';
import MeasurementTypes from '@/enums/measurementTypes';
import { FractionsHelper } from '@/helpers/fractionsHelper';
import { makePatchOperation } from '@/helpers/patchOperations/patchBuilder';
import { RoundingHelper } from '@/helpers/roundingHelper';
import Toast from '@/helpers/toast';
import i18n from '@/i18n';
import { BFormSelectOption } from '@/interfaces/BFormSelectOption';
import MeasurementOption from '@/models/userSettings/measurementOption';
import ToolbarSettings from '@/models/userSettings/toolbarSettings';
import UserSettings from '@/models/userSettings/userSettings';
import { vxManager } from '@/store/store';

export function getFormattedMeasurementValueForLabel(imperialValueToFormat: number | string | null): string {
  const userSettings = vxManager.userSettingsModule?.getUserSettings;
  if (imperialValueToFormat == null || !userSettings) {
    return '';
  }

  const formatter = userSettings.metricMeasurementOn ? formatMetricMeasure : formatImperialMeasure;
  return formatter(imperialValueToFormat);
}

export function formatImperialMeasure(
  imperialValueToFormat: number | string,
  valueInFeet = false,
  verbose = false
): string {
  return FractionsHelper.getFormattedValueWithFraction(imperialValueToFormat as number, valueInFeet, verbose);
}

export function formatMetricMeasure(imperialValueToFormat: number | string): string {
  const valueInMilimeters =
    FractionsHelper.getNumberFromInchesString(imperialValueToFormat.toString()) / MILIMETERS_TO_INCHES;
  return RoundingHelper.roundToNumberOfDecimals(valueInMilimeters, 0).toString() + ' mm';
}

export function getFormattedSelectableOptionForBFormSelectInputs(imperialValueToFormat: number): BFormSelectOption {
  const value = FractionsHelper.getFormattedValueWithFraction(imperialValueToFormat);
  const text = getFormattedMeasurementValueForLabel(imperialValueToFormat);
  return { value, text };
}

export function convertMetricMeasurementOn(metricMeasurementOn: boolean): MeasurementTypes {
  return metricMeasurementOn ? MeasurementTypes.Metric : MeasurementTypes.Imperial;
}

export function getMetricMeasurementOn(measurementType: MeasurementTypes): boolean {
  return measurementType === MeasurementTypes.Metric;
}

/**
 * Persists the working measurement unit to the user settings, toasting on
 * failure. It is a single preference shared by the partitions and lockers
 * drawings, so their unit selectors both patch through here.
 */
export async function patchMeasurementType(measurementType: MeasurementTypes): Promise<void> {
  try {
    await vxManager.userSettingsModule.patchUserSettings([
      makePatchOperation(UserSettings, 'metricMeasurementOn', getMetricMeasurementOn(measurementType)),
    ]);
  } catch {
    Toast.error({ body: i18n.global.t('account.profile.updateSettings.error').toString() });
  }
}

/**
 * Persists the "show both measurements" toggle to the user settings,
 * toasting on failure. Shared by the partitions and lockers drawings, same
 * as `patchMeasurementType`.
 */
export async function patchShowBothMeasurementsTypes(value: boolean): Promise<void> {
  try {
    await vxManager.userSettingsModule.patchToolbarSettings([
      makePatchOperation(ToolbarSettings, 'showBothMeasurementsTypes', value),
    ]);
  } catch {
    Toast.error({ body: i18n.global.t('account.profile.updateSettings.error').toString() });
  }
}

export function getMeasurementType(measurementOption: MeasurementOption): MeasurementTypes {
  if (measurementOption.measurementType.toLowerCase() === MeasurementTypes.Metric) {
    return MeasurementTypes.Metric;
  }
  return MeasurementTypes.Imperial;
}
